import {
  Component,
  ComponentRef,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { take } from 'rxjs';
import { ICallDetailModal } from 'src/app/interfaces/icall-detail-modal';
import { CallDetailsModalComponent } from './call-details-modal/call-details-modal.component';
import { ActivatedRoute, Router } from '@angular/router';
import { IAssignmentModule } from 'src/app/interfaces/iassignment-module';
import { IAssignmentModuleCall } from 'src/app/interfaces/iassignment-module-call';
import { AssignmentService } from 'src/app/services/learner/assignment.service';
import { AppConstants } from 'src/app/app-constants';
import { DeviceService } from 'src/app/services/general/device.service';
import { Subscription } from 'rxjs';
import { AmplitudeService } from 'src/app/services/external-tools/amplitude.service';
import { CallFlagService } from 'src/app/services/learner/call-flag/call-flag.service';
import { TRANSLOCO_SCOPE, TranslocoService } from '@jsverse/transloco';
import { LocaleService } from 'src/app/services/locale.service';
import { HelpscoutBeaconService } from 'src/app/services/external-tools/helpscout-beacon.service';
import { AmplitudeTrackingEvents } from 'src/app/utils/enums/amplitude/amplitudeTrackingEvents.enum';
import { SentryService } from 'src/app/services/logger/sentry.service';
import { NativeCallService } from 'src/app/services/native-call.service';
import { canShareScreen } from 'src/app/utils/device/device.util';

@Component({
  selector: 'app-assignment',
  standalone: false,
  templateUrl: './assignment.component.html',
  styleUrls: ['./assignment.component.scss'],
  providers: [
    {
      provide: TRANSLOCO_SCOPE,
      useValue: 'assignment',
    },
  ],
})
export class AssignmentComponent {
  isLoading: boolean = true;
  assignment: IAssignmentModule | undefined;
  assignmentDaysLeft: number | null = null;
  displaySystemMessage: boolean = false; // TODO: Implement system message display
  callDetails: ICallDetailModal | null = null;
  practiceType: string | null = null;
  displayPreviousResults: boolean = false;
  screenshareBlocked: boolean = false;
  screenshareBlockedCallName: string = '';
  moduleAssignmentUuid: string | null = null;
  selectedCallUuid: string | null = null;
  selectedCallType: string = AppConstants.CALL_TYPE_GUIDED;
  selectedCallName: string = '';
  selectedCall: IAssignmentModuleCall | undefined;
  isIpad: boolean = false;
  private deviceSubscription!: Subscription;
  typePractice: string = AppConstants.TYPE_PRACTICE;
  callType: string = '';
  /**
   * Stores assignment statuses that are NOT "complete" or "active" (i.e., non-operational states).
   * Examples: "staged", "closed", test statuses, etc.
   * This is used to control UI behavior when assignment is in a non-operational state.
   */
  nonOperationalAssignmentStatus: string | null = null;
  private beaconOpenListener?: () => void;

  // Set from the assignment's own value once fetched (see processData()).
  hideResultsForLearners = false;

  @ViewChild('modalContainerCallDetails', { read: ViewContainerRef })
  callDetailsModalContainer!: ViewContainerRef;
  private callDetailsModalRef?: ComponentRef<CallDetailsModalComponent>;
  AppConstants = AppConstants;

  constructor(
    private assignmentService: AssignmentService,
    private router: Router,
    private route: ActivatedRoute,
    private deviceService: DeviceService,
    private amplitudeService: AmplitudeService,
    private callFlagService: CallFlagService,
    private translocoService: TranslocoService,
    protected localeService: LocaleService,
    private helpscoutBeaconService: HelpscoutBeaconService,
    private sentryService: SentryService,
    protected nativeCallService: NativeCallService
  ) {}

  async ngOnInit() {
    this.route.queryParams.subscribe((params) => {
      this.moduleAssignmentUuid = params['moduleAssignmentUuid'];

      if (this.moduleAssignmentUuid) {
        this.fetchAssignment(this.moduleAssignmentUuid);
      } else {
        // Sending the Event of Loading Assignment Page to Amplitude
        this.amplitudeService.trackEvent('assignmentpage_pageview');

        console.error(
          'Module assignment UUID not found in query params, redirecting to learner dashboard'
        );
        this.goToLearnerDashboard();
      }
      const practiceType = params['practiceType'];
      this.practiceType = practiceType;
    });
    this.deviceSubscription = this.deviceService.isIpad$.subscribe((status) => {
      this.isIpad = status;
    });
    localStorage.setItem('returnUrl', encodeURIComponent(this.router.url));
    await this.initBeacon();
  }

  private async initBeacon(): Promise<void> {
    if (this.nativeCallService.isNativeIos) {
      return;
    }
    await this.helpscoutBeaconService.init();
    this.setupBeaconOpenListener();
  }

  private setupBeaconOpenListener(): void {
    this.beaconOpenListener = () => {
      this.onBeaconOpen();
    }

    if((window as any).Beacon) {
      try {
        (window as any).Beacon('on', 'open', this.beaconOpenListener);
        console.log('HelpscoutBeacon: Beacon open listener set');
      } catch (error) {
        console.error('HelpscoutBeacon: Error setting up open listener', error);
      }
    }
  }

  onBeaconOpen(): void {
    this.amplitudeService.trackEvent(
      AmplitudeTrackingEvents.HELPSCOUT_BEACON_OPENED,
      { page_name: this.router.url }
    );
  }

  ngOnDestroy(): void {
    if (this.deviceSubscription) {
      this.deviceSubscription.unsubscribe();
    }
    this.helpscoutBeaconService.destroy();

    if (this.beaconOpenListener && (window as any).Beacon) {
      try {
        (window as any).Beacon('off', 'open', this.beaconOpenListener);
        console.log('HelpscoutBeacon: Beacon open listener removed');
      } catch (error) {
        console.error('HelpscoutBeacon: Error removing open listener', error);
      }
    }
  }

  handleTitleClick(): void {
    localStorage.setItem('returnUrl', '/assignments');
  }

  goToLearnerDashboard(): void {
    localStorage.setItem('returnUrl', '/assignments');
    this.router.navigate(['/assignments']);
  }

  fetchAssignment(moduleAssignmentUuid: string): void {
    this.assignmentService
      .findByModuleAssignmentUuid(moduleAssignmentUuid)
      .subscribe({
        next: (dataResponse: IAssignmentModule | any) => {
          console.log('Assignment fetched successfully: ', dataResponse);
          this.processData(dataResponse);
        },
        error: (err) => {
          console.error('Error occurred in fetching assignment: ', err);
          this.goToLearnerDashboard();
        },
        complete: () => {
          // Sending the Event of Loading Assignment Page to Amplitude
          this.amplitudeService.trackEvent('assignmentpage_pageview', {
            assignment_type: this.assignment?.moduleAssignmentType,
            assignment_duedate: this.assignment?.moduleAssignmentDueDate,
            assignment_status: this.getAssignmentStatus(),
          });

          this.isLoading = false;
        },
      });
  }

  private getTestAssignmentStatus(assignmentStatus: string | null | undefined): string | null {
    if (!assignmentStatus || typeof assignmentStatus !== 'string') {
      return null;
    }

    // SAFE: internal status field vs fixed AppConstants
    const normalizedStatus = assignmentStatus.toLowerCase().trim();
    
    if (
      normalizedStatus !== AppConstants.COMPLETE_ASSIGNMENT_STATUS &&
      normalizedStatus !== AppConstants.ACTIVE_ASSIGNMENT_STATUS
    ) {
      return assignmentStatus;
    }
    
    return null;
  }

  processData(dataResponse: IAssignmentModule | any): void {
    this.assignment = dataResponse;
    this.hideResultsForLearners = !!this.assignment?.hideResultsForLearners;

    this.nonOperationalAssignmentStatus = this.getTestAssignmentStatus(
      this.assignment?.status
    );

    // Pre-compute isLastAttemptToday for each call
    if (this.assignment?.callsWithinModuleAssignnment) {
      this.assignment.callsWithinModuleAssignnment.forEach((call) => {
        call.isLastAttemptToday = this.isLastAttemptToday(call);
      });
    }

    if (this.assignment) {
      // Calculate days left for assignment due date
      if (this.assignment.moduleAssignmentDueDate) {
        const assignmentDate = new Date(
          this.assignment.moduleAssignmentDueDate
        );
        const now = new Date();
        const timeDiff = assignmentDate.getTime() - now.getTime();
        this.assignmentDaysLeft = Math.ceil(timeDiff / (1000 * 3600 * 24));

        console.log('Assignment days left: ', this.assignmentDaysLeft);
      }

      // order calls by callOrder
      this.assignment.callsWithinModuleAssignnment.sort(
        (a, b) => a.callOrder - b.callOrder
      );

      // set isMaxAttemptsReached for each call
      this.assignment.callsWithinModuleAssignnment.forEach((call) => {
        call.isMaxAttemptsReached =
          this.callFlagService.isMaxAttemptsReached(call);
        call.isIncompleteMessageVisible =
          this.callFlagService.isIncompleteMessageVisible(call, this.assignment?.scoreCriteria);
        const isTestAssignment = this.assignment?.status === AppConstants.AUTHOR_TESTING_ASSIGNMENT_STATUS_UPPER;
        call.pauseEnabled = isTestAssignment || !!this.assignment?.pauseEnabled;
        call.recentCallAttemptDate = call.recentCallAttemptDate
          ? this.localeService.formatDate(call.recentCallAttemptDate)
          : '';
        this.translocoService
          .selectTranslate('NOT_STARTED', {}, 'assignment')
          .pipe(take(1))
          .subscribe((translated) => {
            call.callTypeTranslated = this.getCallTypeKey(call.callType);
            call.statusBadge = this.getCallStatusBadge(call);
          });
      });

      // set in local storage the callStarted for the assignment
      this.registerCallStarted();

      // set in local storage the flags for each call
      this.callFlagService.saveCallFlags(
        this.assignment.callsWithinModuleAssignnment,
        this.assignment?.scoreCriteria
      );
    }
  }

  isScreenshareUnsupported(call: IAssignmentModuleCall): boolean {
    return (
      !this.nativeCallService.isNativeIos &&
      !!call.screenshareEnabled &&
      !canShareScreen()
    );
  }

  showScreenshareUnsupportedModal(call: IAssignmentModuleCall): void {
    this.screenshareBlockedCallName = call.callName;
    this.screenshareBlocked = true;
  }

  closeScreenshareUnsupportedModal(): void {
    this.screenshareBlocked = false;
  }

  goToCall(call: IAssignmentModuleCall): void {
    if (this.isScreenshareUnsupported(call)) {
      this.showScreenshareUnsupportedModal(call);
      return;
    }

    // Sending the Event of Clicking on Launch Call to Amplitude
    this.amplitudeService.trackEvent('assignmentpage_click_launchcall', {
      call_status: call.callStatus,
      assignment_status: this.getAssignmentStatus(),
      assignment_type: this.assignment?.moduleAssignmentType,
    });

    const moduleAssignmentUuid = this.assignment?.moduleAssignmentUuid;
    const callUuid = call.callUuid;

    // Open the Sentry call-session transaction the moment the user clicks Launch.
    // This captures the full journey: navigation to /call, device setup, and the
    // active simulation — not just the API portion.
    if (moduleAssignmentUuid && callUuid) {
      this.sentryService.startCallSession(callUuid, moduleAssignmentUuid);
    }
    const callType = call.callType;
    // For PATHWAY type, use the assignment type as practiceType
    const practiceType = this.assignment?.moduleAssignmentType === 'PATHWAY' 
      ? 'PATHWAY' 
      : this.practiceType;
    const returnUrl = `/call?moduleAssignmentUuid=${moduleAssignmentUuid}&callUuid=${callUuid}&practiceType=${practiceType}&callType=${callType}`;
    localStorage.setItem('returnUrl', encodeURIComponent(returnUrl));
    this.router.navigate([`/call`], {
      queryParams: {
        moduleAssignmentUuid,
        callUuid,
        practiceType,
        callType,
      },
    });
  }

  openModal(call: IAssignmentModuleCall) {
    if (!this.callDetailsModalContainer) {
      console.error('callDetailsModalContainer not found');
      return;
    }

    this.callDetailsModalContainer.clear();
    this.callDetailsModalRef = this.callDetailsModalContainer.createComponent(
      CallDetailsModalComponent
    );
    // Pass data to the modal
    if (this.callDetailsModalRef.instance) {
      this.callDetailsModalRef.instance.callDetails = this.callDetails;
      this.callDetailsModalRef.instance.moduleAssignmentUuid =
        this.assignment?.moduleAssignmentUuid;
      this.callDetailsModalRef.instance.callType = call.callType;
      this.callDetailsModalRef.instance.practiceType = this.practiceType;
      this.callDetailsModalRef.instance.call_status = call.callStatus;
      this.callDetailsModalRef.instance.assignment_status =
        this.getAssignmentStatus();
      this.callDetailsModalRef.instance.assignment_type =
        this.assignment?.moduleAssignmentType;
      this.callDetailsModalRef.instance.isMaxAttemptsReached =
        !!call.isMaxAttemptsReached;
      this.callDetailsModalRef.instance.nonOperationalAssignmentStatus =
        this.nonOperationalAssignmentStatus;
      this.callDetailsModalRef.instance.isScreenshareUnsupported =
        this.isScreenshareUnsupported(call);
      this.callDetailsModalRef.instance.screenshareUnsupportedEvent
        .pipe(take(1))
        .subscribe(() => {
          this.closeModal();
          this.showScreenshareUnsupportedModal(call);
        });
      // Listen for close event
      this.callDetailsModalRef.instance.closeModalEvent
        .pipe(take(1))
        .subscribe(() => {
          this.closeModal();
        });
    }
  }

  closeModal() {
    if (this.callDetailsModalRef) {
      this.callDetailsModalRef.destroy();
      this.callDetailsModalRef = undefined;
    }
    this.callDetailsModalContainer.clear();
  }

  getCallDetails(call: IAssignmentModuleCall) {
    // Sending the Event of Clicking on 'View Call Details' to Amplitude
    this.amplitudeService.trackEvent('assignmentpage_click_calldetails', {
      call_status: call.callStatus,
      assignment_status: this.getAssignmentStatus(),
      assignment_type: this.assignment?.moduleAssignmentType,
    });

    const moduleAssignmentUuid = this.assignment?.moduleAssignmentUuid;
    const callUuid = call.callUuid;

    this.assignmentService
      .getCallDetails(callUuid, moduleAssignmentUuid)
      .subscribe({
        next: (res: ICallDetailModal) => {
          this.callDetails = res;
          this.openModal(call);
          console.log(
            `Response received successfully for call details: ${res}`
          );
        },
        error: (err: any) => {
          console.error('Error occurred in getting call details:  ', err);
        },
      });
  }

  openPreviousResultsModal(call: IAssignmentModuleCall): void {
    // Sending the Event of Clicking on Previous Results to Amplitude
    this.amplitudeService.trackEvent('assignment_click_previousresults', {
      call_status: call.callStatus,
      assignment_status: this.getAssignmentStatus(),
      assignment_type: this.assignment?.moduleAssignmentType,
    });
    const attempts = call.numberOfPreviousAttempts;
    console.log(`Click open previous results with ${attempts} attempts`, call);
    if (attempts && attempts > 0) {
      this.displayPreviousResults = true;
      this.selectedCallUuid = call.callUuid;
      this.selectedCallType = call.callType;
      this.selectedCallName = call.callName;
      this.selectedCall = call;
    }
  }

  closePreviousResultsModal() {
    this.displayPreviousResults = false;
  }

  registerCallStarted(): void {
    if (!this.assignment) {
      return;
    }
    // Store the call UUIDs of all calls that have been started.
    this.assignment.callsWithinModuleAssignnment.forEach((call) => {
      if (call.numberOfPreviousAttempts > 0) {
        const uuid = call.callUuid;
        const callStarted: string[] = JSON.parse(
          localStorage.getItem('callStarted') || '[]'
        );

        if (!callStarted.includes(uuid)) {
          callStarted.push(uuid);
          localStorage.setItem('callStarted', JSON.stringify(callStarted));
        }
      }
    });
  }

  getAssignmentStatus(): string {
    if (this.assignment) {
      const assignmentStatusComplete =
        this.assignment.callsWithinModuleAssignnment.every(
          (call) =>
            call.callStatus === 'COMPLETE' || call.callStatus === 'CERTIFIED'
        );
      const assignmentStatusNotStarted =
        this.assignment.callsWithinModuleAssignnment.every(
          (call) => call.callStatus === 'NOT_STARTED'
        );
      const assignmentStatusOverdue =
        !assignmentStatusComplete &&
        this.assignment.moduleAssignmentDueDate &&
        this.assignmentDaysLeft !== null &&
        this.assignmentDaysLeft < 0;

      if (assignmentStatusOverdue) {
        return 'Overdue';
      } else if (assignmentStatusComplete) {
        return 'Complete';
      } else if (assignmentStatusNotStarted) {
        return 'Not Started';
      } else {
        return 'Incomplete';
      }
    }
    return 'Not Started';
  }

  isAssignmentFinished(): boolean {
    return this.assignment?.callsWithinModuleAssignnment
      ? this.assignment.callsWithinModuleAssignnment.every(
          (call) =>
            call.callStatus === 'COMPLETE' ||
            call.callStatus === 'IN_COMPLETE' ||
            call.callStatus === 'CERTIFIED'
        )
      : false;
  }

  getCallStatusBadge(call: IAssignmentModuleCall): {
    text: string;
    class: string;
  } {
    // Check if call is CLOSED first
    // SAFE: internal status field vs fixed AppConstants
    if ((this.nonOperationalAssignmentStatus || '').toLowerCase() === AppConstants.CLOSED_ASSIGNMENT_STATUS) {
      return {
        text: `${this.translocoService.translate(
          'CLOSED',
          {},
          'assignment'
        )}`,
        class: 'badge-gray',
      };
    }
    
    // SAFE: internal status field vs fixed AppConstants
    if (
      this.nonOperationalAssignmentStatus?.toLowerCase() ===
      AppConstants.STAGED_ASSIGNMENT_STATUS
    ) {
      return {
        text: `${this.translocoService.translate(
          'NOT_STARTED',
          {},
          'assignment'
        )}`,
        class: '',
      };
    }
    if (
      this.nonOperationalAssignmentStatus &&
      this.getTestAssignmentStatus(this.nonOperationalAssignmentStatus)
    ) {
      return {
        text: `${this.translocoService.translate(
          'TEST',
          {},
          'assignment'
        )}`,
        class: 'badge-purple',
      };
    }
    const type = this.assignment?.moduleAssignmentType;
    if (type === AppConstants.TYPE_PRACTICE) {
      return this.getPracticeCallStatus(call);
    } else if (type === AppConstants.TYPE_CERTIFICATION) {
      return this.getCertificationCallStatus(call);
    } else {
      return { text: '', class: '' };
    }
  }

  getCommonCallStatuses(): Record<string, { text: string; class: string }> {
    return {
      CLOSED: {
        text: `${this.translocoService.translate(
          'CLOSED',
          {},
          'assignment'
        )}`,
        class: 'badge-gray',
      },
      NOT_STARTED: {
        text: `${this.translocoService.translate(
          'NOT_STARTED',
          {},
          'assignment'
        )}`,
        class: '',
      },
      IN_PROGRESS: {
        text: `${this.translocoService.translate(
          'IN_PROGRESS',
          {},
          'assignment'
        )}`,
        class: 'badge-warning',
      },
      IN_COMPLETE: {
        text: `${this.translocoService.translate(
          'NO_ATTEMPTS_LEFT',
          {},
          'assignment'
        )}`,
        class: 'badge-error',
      },
    };
  }

  getPracticeCallStatus(call: IAssignmentModuleCall): {
    text: string;
    class: string;
  } {
    const specificStatuses: Record<string, { text: string; class: string }> = {
      COMPLETE: {
        text: `${this.translocoService.translate(
          'COMPLETED',
          {},
          'assignment'
        )}`,
        class: 'badge-success',
      },
    };

    const statusMap = { ...this.getCommonCallStatuses(), ...specificStatuses };
    return statusMap[call.callStatus] || { text: '', class: '' };
  }

  getCertificationCallStatus(call: IAssignmentModuleCall): {
    text: string;
    class: string;
  } {
    const specificStatuses: Record<string, { text: string; class: string }> = {
      CERTIFIED: {
        text: `${this.translocoService.translate(
          'CERTIFIED',
          {},
          'assignment'
        )}`,
        class: 'badge-success',
      },
    };

    const statusMap = { ...this.getCommonCallStatuses(), ...specificStatuses };
    return statusMap[call.callStatus] || { text: '', class: '' };
  }

  getCallTypeKey(callType: string): string | undefined {
    if (callType === 'GUIDED') {
      return this.translocoService.translate(
        'GUIDED_SIMULATION',
        {},
        'assignment'
      );
    } else if (callType === 'CONVERSATIONAL') {
      return this.translocoService.translate(
        'CONVERSATIONAL_SIMULATION',
        {},
        'assignment'
      );
    }
    return this.translocoService.translate(callType, {}, 'assignment');
  }


  isLastAttemptToday(call: IAssignmentModuleCall): boolean {
    if (!call?.recentCallLaunchDate) return false;

    const attemptDate = new Date(call.recentCallLaunchDate);
    const today = new Date();

    const normalize = (d: Date) =>
      new Date(d.getFullYear(), d.getMonth(), d.getDate());

    return normalize(attemptDate).getTime() === normalize(today).getTime();
  }

  handleLaunchClick(call: IAssignmentModuleCall): void {
    if (!call.hasDetails || call.isLastAttemptToday) {
      this.goToCall(call);
      return;
    }

    this.getCallDetails(call);
  }
}
